import { Suspense, useRef } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { Image } from '@/routes/-components/image';
import { trpc } from '@/lib/trpc';
import { useTranslation } from '@/lib/i18n';
import { formatBandFigure, formatCompactMoney } from '../figure-format';

type SuccessCase = {
	company: string;
	/** Both the tab's logo and the two figures come from this portal. */
	portalId: string;
	quote: string;
	author?: { name: string; role: string };
};

/**
 * Exported so the route warms their logos. Also `getWorldcooGlobalStats`' `featuredMerchants`: a merchant
 * added here draws no figures until it is added there too.
 */
export const SUCCESS_CASE_PORTAL_IDS = ['eroski', 'correos', 'areas', 'ticketmaster', 'bonpreu'] as const;

/** The tab's inner width. */
const LOGO_WIDTH = 132;

/** Laid out at the record's own `width`/`height`; `contain` guards a record whose numbers disagree with its file. */
function CaseLogo({ portalId, company }: { portalId: string; company: string }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const { data } = useSuspenseQuery(trpc.portal.getAuthor.queryOptions({ portalId, locale }));
	const logo = 'notFound' in data ? undefined : data.logo;
	if (!logo) return null;

	// Dimensions are optional upstream; 2:1 is what every logo box normalises to (`partner-logo.tsx`).
	const aspect = logo.width && logo.height ? logo.width / logo.height : 2;
	return (
		<Image
			src={logo.location}
			alt={company}
			height={Math.round(LOGO_WIDTH / aspect)}
			aspectRatio={aspect}
			objectFit="contain"
			className="max-h-15 w-full object-contain! mix-blend-multiply"
		/>
	);
}

function Figure(props: { value: string; label: string }) {
	return (
		<div className="text-right">
			<p className="text-4xl leading-[1.1] font-medium tracking-[-0.02em] tabular-nums md:text-5xl">{props.value}</p>
			{/* The md frame is exactly 74px (53 + 21), and a 320 viewport leaves 79px, too narrow for "financiados" at 16px. */}
			<p className="text-ink/50 text-sm leading-[1.3] tracking-[-0.02em] md:text-base">{props.label}</p>
		</div>
	);
}

/**
 * The homepage's own `featuredMerchants` payload, so the two never publish different numbers. A running
 * campaign counts as funded. Nothing is drawn on a BI failure (`[]`): "0 funded projects" would be false.
 */
function CaseFigures(props: { portalId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	const merchant = stats.featuredMerchants.find((m) => m.portalId === props.portalId);
	if (!merchant) return null;

	return (
		<>
			<Figure value={formatBandFigure(language, merchant.projectsFunded)} label={t`funded projects`} />
			{/* Abbreviated: written in full, the total does not fit the pair's line of a 586px card. */}
			<div className="hidden md:block">
				<Figure value={formatCompactMoney(language, merchant.totalRaised.amount)} label={t`raised`} />
			</div>
		</>
	);
}

/**
 * The concave bite is a block painted in the section's background, the cheapest inverted radius; on any
 * background but `bg-white` the notch becomes a white smear.
 */
function CaseCard(props: { item: SuccessCase }) {
	const item = props.item;
	return (
		<article className="relative flex h-full w-[85vw] flex-col md:min-h-[398px] md:w-[586px]">
			<div className="bg-apricot absolute inset-0 rounded-3xl" />
			<div className="absolute top-0 left-0 h-[92px] w-[161px] rounded-br-3xl bg-white" />

			{/* The convex junction's sliver, which no border-radius draws; the 0.5px feather stops aliasing. */}
			<div className="absolute top-0 left-[161px] size-6 bg-[radial-gradient(circle_at_bottom_right,transparent_23.5px,white_24px)]" />
			<div className="absolute top-[92px] left-0 size-6 bg-[radial-gradient(circle_at_bottom_right,transparent_23.5px,white_24px)]" />

			{/* The hand-off greyscales the logo, which brands rarely allow: ask before restoring it. */}
			<div className="bg-sand absolute top-0 left-0 isolate flex h-[79px] w-[148px] items-center justify-center rounded-[17px] px-2">
				<Suspense fallback={null}>
					<CaseLogo portalId={item.portalId} company={item.company} />
				</Suspense>
			</div>

			{/* On mobile one figure (two need ~300px of ~288), and `left` stops a 157px label growing under the tab. */}
			<div className="absolute top-8 right-8 left-[169px] flex justify-end gap-6 md:left-auto">
				<Suspense fallback={null}>
					<CaseFigures portalId={item.portalId} />
				</Suspense>
			</div>

			{/* Clears the absolute stat block: 170px is the mockup's y, 120 clears 32 + 40 + 21 on mobile. */}
			<div className="relative flex h-full flex-col p-8 pt-[120px] md:pt-[170px]">
				<blockquote className="text-base leading-[1.5] font-medium tracking-[-0.02em]">{item.quote}</blockquote>

				{item.author && (
					<figcaption className="mt-auto pt-6 text-sm leading-[1.5] tracking-[-0.02em]">
						<span className="block font-medium">{item.author.name}</span>
						<span className="text-ink/50 block text-pretty">{item.author.role}</span>
					</figcaption>
				)}
			</div>
		</article>
	);
}

/** The `Carousel`'s own buttons are off: the hand-off puts the arrows in the section header. */
export function SuccessCases() {
	const { t } = useLingui();
	const scroller = useRef<HTMLUListElement>(null);

	const cases: SuccessCase[] = [
		{
			company: 'Eroski',
			portalId: 'eroski',
			quote: t`“Charity Rounding Up is a highly effective programme and a great tool for our customers to act on their commitment to different social causes, in an easy way.”`,
			author: { name: 'Alejandro Martínez Berriochoa', role: t`Sustainability Director at Grupo Eroski` },
		},
		{
			company: 'Correos',
			portalId: 'correos',
			quote: t`“The social causes Correos champions are aligned with the company's strategy and values. We work with as many NGOs as we can, and when an emergency hits we throw ourselves into it. Being flexible and trusting each other matters: we all want to make a positive impact.”`,
			author: { name: 'Elena Fernández', role: t`Deputy Director of International Relations and ESG at Correos` },
		},
		{
			company: 'Areas',
			portalId: 'areas',
			quote: t`“Guided by the belief that small actions make a big difference, at Areas we are actively committed to social causes, with the aim of contributing to a much more caring society.”`,
			author: { name: 'Sergio Rodríguez', role: t`CEO of Areas Iberia` },
		},
		{
			company: 'Ticketmaster',
			portalId: 'ticketmaster',
			quote: t`“Every month at Ticketmaster we commit to a solidarity project. National and international NGOs alike face social and environmental challenges affecting thousands of people around the world. We care about that, a lot. And we know you do too.”`,
			author: { name: 'Reyes Sáenz de Juano', role: t`Sales & Marketing Director at Ticketmaster Spain` },
		},
		{
			company: 'Bonpreu Esclat',
			portalId: 'bonpreu',
			quote: t`“We have spent the last few months working on the Charity Rounding Up solution and we are very happy with the results. Our customers are donating easily and regularly. Our partnership with Worldcoo fits our DNA perfectly and we will be working together for many years to come.”`,
			author: { name: 'Manel Prat', role: t`Marketing & PR Director at Bonpreu Esclat Group` },
		},
	];

	const scrollBy = (direction: 1 | -1) => {
		const el = scroller.current;
		if (!el) return;
		// Measured between two items, so it carries the gap for the desktop and mobile card alike.
		const [first, second] = [el.children[0], el.children[1]] as (HTMLElement | undefined)[];
		const step = first && second ? second.offsetLeft - first.offsetLeft : el.clientWidth;
		el.scrollBy({ left: direction * step, behavior: 'smooth' });
	};

	return (
		<section className="bg-white py-16 md:py-20">
			<div className="mx-auto flex max-w-[1440px] flex-col items-start gap-10 px-6 md:flex-row md:items-end md:justify-between md:gap-6 xl:px-10">
				<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
					{t`Our success stories`}
				</h2>
				<div className="flex shrink-0 gap-2">
					<button
						type="button"
						onClick={() => scrollBy(-1)}
						aria-label={t`Previous story`}
						className="border-ink/40 hover:bg-ink/5 grid size-8 place-items-center rounded-full border transition-colors"
					>
						<ArrowLeft className="size-4" />
					</button>
					<button
						type="button"
						onClick={() => scrollBy(1)}
						aria-label={t`Next story`}
						className="border-ink/40 hover:bg-ink/5 grid size-8 place-items-center rounded-full border transition-colors"
					>
						<ArrowRight className="size-4" />
					</button>
				</div>
			</div>

			<Carousel ref={scroller} className="carousel-no-buttons mt-4 items-stretch gap-8 pb-2 md:mt-10 xl:px-10">
				{cases.map((item) => (
					<CarouselItem key={item.company} className="flex">
						<CaseCard item={item} />
					</CarouselItem>
				))}
			</Carousel>
		</section>
	);
}
